/* The demo line, for whichever business this link is for.
 *
 * ONE PAGE, MANY BUSINESSES. `/call/<slug>` (functions/call/[slug].js) serves this same bundle
 * with a <script id="demo" type="application/json"> carrying the slug and the brand; the
 * page then asks /api/config?slug= for the name, the assistant and the openers, and
 * dresses itself. Every call is started with the slug.
 *
 * THE PASSCODE IS OFF unless one was minted for this demo: /api/config answers
 * `passcodeRequired` from whether the row has a hash, and the gate screen below only
 * appears for the demos that do.
 *
 * BOTH DIRECTIONS, ON ONE PAGE. "Answers your calls" is the demo this page has always
 * been: you ring, the assistant answers. "Calls your customers" is the other half - you
 * pick one of the spec's SITUATIONS, edit your name and what the office wants passed on,
 * and the assistant rings the handset drawn here. The switch is hidden entirely unless
 * this demo has situations (`config.outbound.web`), because a switch with one working
 * side offers something and then says no.
 *
 * THE LABELS NAME A CAPABILITY, NOT A PRONOUN. They used to read "Call them" and "Have
 * them call you", where "them" was the business - while the card below said "What you
 * want them told", where it was the customer. One word, two people, on a page whose
 * reader has met neither. Every visible string names its actor now, and
 * tests/test_client_contract.py fails the page if a bare "they" comes back.
 *
 * The transport is the Pipecat JS SDK over Daily WebRTC. The protocol, in full:
 *   GET  /api/config?slug=             -> {business, assistant, page, situations, ...}
 *   POST /api/session {slug, passcode} -> {token, expiresIn}
 *   POST /api/connect {slug, token, visit}  -> {dailyRoom, dailyToken, sessionId}
 *   POST /api/dial {slug, token, visit, situation_id, contact_name, message}  -> the same
 *   POST /api/visit {slug, visit, event, seconds?}  -> 204, the page reporting on its visit
 *   client.connect(that)               -- DailyTransport maps the keys itself.
 *
 * THE ONE ASYMMETRY IN THE CALL ITSELF: outbound, the microphone stays SHUT and nothing
 * is answered until the visitor taps Answer. The bot is in the room for the whole ring -
 * that is what makes it able to speak the instant it is picked up - and a live microphone
 * before then is a room somebody is being listened to in without having agreed to it.
 *
 * Under server.py (a laptop) there is no slug: the SDK's demo server answers the same
 * three routes for the business INBOUND_BRIEF_FILE names, and the page dresses itself
 * with whatever /api/config carries.
 *
 * Read call-kit.js's audio note before touching anything that plays sound. Echo
 * cancellation depends on where the bot's voice is routed, and it fails silently.
 */

import { PipecatClient } from "@pipecat-ai/client-js";
import { DailyTransport } from "@pipecat-ai/daily-transport";

import {
  accentColour,
  clock,
  createCaptions,
  createNotes,
  createRinger,
  createToken,
  openAudioContext,
  postJson,
  reportAudioConstraints,
  startWave,
  tap,
} from "./call-kit.js";
import { renderOpeners } from "./readout-kit.js";

(() => {
  "use strict";

  const $ = (id) => document.getElementById(id);

  const el = {
    screens: {
      gate: $("screen-gate"),
      dialer: $("screen-dialer"),
      idle: $("screen-idle"),
      ringing: $("screen-ringing"),
      call: $("screen-call"),
      ended: $("screen-ended"),
    },
    phoneScreen: $("phone-screen"),
    islandTimer: $("island-timer"),
    gateForm: $("gate-form"),
    passcode: $("passcode"),
    gateError: $("gate-error"),
    dialError: $("dial-error"),
    pageError: $("page-error"),
    callButton: $("call-button"),
    liveDisc: $("live-disc"),
    wave: $("wave"),
    callState: $("call-state"),
    callTimer: $("call-timer"),
    speakingLabel: $("speaking-label"),
    captions: $("captions"),
    muteButton: $("mute-button"),
    muteLabel: $("mute-label"),
    endButton: $("end-button"),
    limitNote: $("limit-note"),
    ringNote: $("ring-note"),
    openerList: $("opener-list"),
    openers: $("openers"),
    endedEyebrow: $("ended-eyebrow"),
    endedTitle: $("ended-title"),
    endedSub: $("ended-sub"),
    remoteAudio: $("remote-audio"),
    brandLogo: $("brand-logo"),
    brandName: $("brand-name"),
    contactName: $("contact-name"),
    callName: $("call-name"),
    lede: $("lede"),
    ledeSub: $("lede-sub"),
    initials: [$("avatar-initials"), $("orb-initials"), $("ended-initials"),
               $("idle-initials"), $("ringing-initials")],
    // the other direction
    modeSwitch: $("mode-switch"),
    modeIn: $("mode-in"),
    modeOut: $("mode-out"),
    outboundCard: $("outbound-card"),
    cardBusiness: $("card-business"),
    labelSituation: $("label-situation"),
    situationChips: $("situation-chips"),
    messageChips: $("message-chips"),
    cardContext: $("card-context"),
    cardName: $("card-name"),
    cardMessage: $("card-message"),
    dialButton: $("dial-button"),
    dialStatus: $("dial-status"),
    dialErrorOut: $("dial-error-out"),
    ringingName: $("ringing-name"),
    answerButton: $("answer-button"),
    declineButton: $("decline-button"),
  };

  /* ── which demo this is ────────────────────────────────────────── */

  // Injected by functions/call/[slug].js. Absent under server.py, where there is no slug.
  const boot = (() => {
    try {
      const node = document.getElementById("demo");
      return node ? JSON.parse(node.textContent || "{}") : {};
    } catch {
      return {};
    }
  })();
  const slug = boot.slug || "";

  /* ── the visit ─────────────────────────────────────────────────── */

  /* `/call/<slug>` wrote a row for this load and put its id in the boot JSON; the page says
   * two things about it (functions/api/visit.js): that it ran at all - which is how the
   * console tells a person from a link scanner - and how long it was looked at, each time
   * it is hidden. The call routes get the id too, to mark the visit that rang.
   *
   * TELEMETRY NEVER BREAKS THE PAGE. It runs inline in the page's start-up, so an exception
   * here would stop everything below it - the config, the buttons, the call. So the whole
   * of it is one fenced function, every callback it registers is fenced again, nothing is
   * awaited, and the one thing the rest of the page takes from it - `visit`, for the call
   * routes - is a plain string that cannot throw. */
  const visit = typeof boot.visit === "string" ? boot.visit : "";
  const quietly = (fn) => (...args) => {
    try {
      fn(...args);
    } catch (_) { /* a page that cannot report still works */ }
  };
  quietly(() => {
    let visibleMs = 0;
    let visibleSince = document.visibilityState === "visible" ? performance.now() : 0;
    const report = (event, extra = {}) => {
      if (!visit || !slug) return;
      const body = JSON.stringify({ slug, visit, event, ...extra });
      if (event === "leave" && navigator.sendBeacon && navigator.sendBeacon("/api/visit", body)) return;
      fetch("/api/visit", { method: "POST", body, keepalive: true }).catch(() => {});
    };
    const onHidden = quietly(() => {
      if (!visibleSince) return;
      visibleMs += performance.now() - visibleSince;
      visibleSince = 0;
      report("leave", { seconds: Math.round(visibleMs / 1000) });
    });
    document.addEventListener("visibilitychange", quietly(() => {
      if (document.visibilityState === "hidden") onHidden();
      else if (!visibleSince) visibleSince = performance.now();
    }));
    window.addEventListener("pagehide", onHidden);
    quietly(report)("seen");
    // `?me=1` (the console's "Open (not counted)") has set its cookie by now; a copied
    // address bar should not carry it on to somebody else.
    if (new URLSearchParams(location.search).has("me")) {
      const url = new URL(location.href);
      url.searchParams.delete("me");
      history.replaceState(history.state, "", url.pathname + url.search + url.hash);
    }
  })();

  let config = { passcodeRequired: true, maxCallSeconds: 300, business: { name: "" } };
  let business = (boot.business && boot.business.name) || "the business";

  /* THE TOKEN IS MINTED WHEN THE CALL IS PLACED, not when the page loads. A demo link is
   * read, left open in a tab, and pressed much later; a token minted on the way in has
   * lapsed by then and /api/connect refuses it. See createToken() in call-kit.js.
   *
   * `passcode` is the code the visitor typed at the gate and the server accepted. It lives
   * here, in this closure, for as long as the tab is open and NOWHERE else - no storage, no
   * cookie, no URL - because every re-mint presents it to /api/session again, which checks
   * the hash again. That is stronger than what it replaces: a code switched off in the
   * console now stops an open tab calling within one token's life, instead of having been
   * asked for once, an hour ago, and never since. */
  let passcode = "";
  const sessionToken = createToken(() => postJson("/api/session", { slug, passcode }));

  // The seven variables brand.css declares. `primary` carries both the green and the
  // blue roles: this page has no second colour system, only the business's.
  const BRAND_VARS = {
    primary: ["--green", "--blue"],
    primary_deep: ["--green-deep", "--blue-deep"],
    accent: ["--leaf"],
    wash: ["--green-wash", "--blue-wash"],
  };

  function paint(brand) {
    if (!brand) return;
    for (const [key, names] of Object.entries(BRAND_VARS)) {
      const value = brand[key];
      if (!/^#[0-9a-fA-F]{6}$/.test(value || "")) continue;
      for (const name of names) document.documentElement.style.setProperty(name, value);
    }
  }

  function initialsOf(name) {
    const words = String(name || "").split(/\s+/).filter(Boolean);
    return (words.slice(0, 2).map((w) => w[0]).join("") || "D").toUpperCase();
  }

  /** Dress the page for one business. Called with the boot data, then with /api/config. */
  function dress({ name, kind, page }) {
    if (name) business = name;
    el.brandName.textContent = business;
    el.contactName.textContent = business;
    el.callName.textContent = business;
    el.endedTitle.textContent = business;
    el.callButton.setAttribute("aria-label", `Call ${business}`);
    for (const node of el.initials) node.textContent = initialsOf(business);
    document.title = `Call ${business}`;
    // `business` DEFAULTS TO THE LOWERCASE "the business", because it is also a contact
    // name, a tab title and a caller ID. The lede is the one place it heads a sentence,
    // and `functions/call/[slug].js` does not inject a name under server.py. Capitalising
    // the variable itself would mangle a brand that is genuinely lowercase.
    const ledeName = business === "the business" ? "The business" : business;
    el.lede.textContent = `${ledeName} answers the phone.`;
    // THE ONLY PLACE THE PAGE SAYS WHAT IT IS SELLING. The "What this is" block at the
    // foot of the column used to, and it is gone: it restated the page above it, below
    // the fold, where a cold reader never got to. So the subhead carries both halves -
    // what the thing is, and what to do about it in the next five seconds.
    if (kind) {
      el.ledeSub.textContent = `An AI phone assistant, built for ${kind === "business" ? "this business" : `a ${kind}`}. `
        + "Be the customer for a minute: press the green button and ask it anything.";
    }
    // Mode-invariant, so it is written here rather than in writeLede(): who is ringing
    // does not change when the switch moves, only which side of the call is on show.
    el.labelSituation.textContent = `Why ${business} is ringing`;
    // Kept so the switch can put it back: the other mode rewrites both lines, and a page
    // that could only be read one way round would be a switch that half works.
    inboundLede = { title: el.lede.textContent, sub: el.ledeSub.textContent };
    if (mode === "out") writeLede();
    if (page) {
      paint(page.brand);
      if (page.logo_url) {
        el.brandLogo.src = page.logo_url;
        el.brandLogo.alt = business;
        el.brandLogo.hidden = false;
        el.brandName.hidden = true;
      }
      renderOpeners(el.openerList, page.openers || []);
      el.openers.hidden = !(page.openers || []).length;
    }
  }

  /* ── which way the call goes ─────────────────────────────────────── */

  /* WHICH MODE IS OPEN, IN THE URL. `?mode=outbound` on the demo link opens straight into
   * "calls your customers"; anything else (including no parameter) is the inbound demo,
   * which is what every link sent before today is.
   *
   * It is written with `replaceState` rather than pushed, so the switch does not fill the
   * back button with a history of taps - but a reload, a bookmark and a pasted link all
   * come back to the mode the page was left in. A salesperson sending "open this and press
   * the green button" and one sending "open this and it rings you" are sending different
   * links now, which is the point.
   *
   * The parameter is READ ONLY WHERE THE MODE EXISTS: a demo with outbound off opens
   * inbound whatever the URL says, rather than showing a mode with no switch to leave it. */
  const MODE_PARAM = "mode";
  const OUTBOUND_MODE = "outbound";

  const wantsOutbound = () =>
    new URLSearchParams(location.search).get(MODE_PARAM) === OUTBOUND_MODE;

  function rememberMode(next) {
    const url = new URL(location.href);
    if (next === "out") url.searchParams.set(MODE_PARAM, OUTBOUND_MODE);
    else url.searchParams.delete(MODE_PARAM);
    history.replaceState(history.state, "", url);
  }

  /** What the card asks for when nobody has typed a name. See where it is filled in. */
  const DEFAULT_NAME = "Sam";

  /** "in" - you ring the assistant. "out" - it rings you. */
  let mode = "in";
  let situation = null;
  let inboundLede = null;

  /* THE PAGE SAYS WHICH DEMO IT IS, not just the switch. "X answers the phone" is a
   * sentence about a call you place, and leaving it up while the card underneath offers
   * the opposite is the page arguing with itself.
   *
   * BOTH SUBHEADS CAST THE ROLE AS AN INSTRUCTION ("be the customer"), never as a fact
   * about the reader ("you are the customer"). The switch above says "your calls", where
   * "you" is the owner reading this; a subhead that then redefines "you" as the customer
   * is the same pronoun bug this page was rewritten to remove, one line higher up. */
  function writeLede() {
    if (mode === "out") {
      el.lede.textContent = `${business} rings a customer.`;
      el.ledeSub.textContent = "Be that customer for a minute: write what the office wants "
        + "passed on, then answer the phone when it rings.";
      return;
    }
    if (inboundLede) {
      el.lede.textContent = inboundLede.title;
      el.ledeSub.textContent = inboundLede.sub;
    }
  }

  /** The screen this mode sits at between calls: a dialer to press, or a phone to wait on. */
  const idleScreen = () => (mode === "out" ? "idle" : "dialer");

  /* "Something else": the chip that hands the card over instead of filling it.
   *
   * The drafted situations are what a salesperson shows; this is what the PROSPECT asks
   * for thirty seconds later - "would it ring my customers about X?" - and a demo that
   * cannot answer that sends them away to imagine the answer. `/api/dial` knows the id and
   * caps what comes back. */
  const CUSTOM = {
    id: "custom",
    label: "Something else",
    context: "",
    messages: [],
    custom: true,
  };

  function renderSituations() {
    const situations = [...(config.situations || []), CUSTOM];
    el.situationChips.replaceChildren();
    for (const item of situations) {
      const li = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "situation-chip";
      button.textContent = item.label;
      button.dataset.situation = item.id;
      button.setAttribute("aria-pressed", "false");
      button.addEventListener("click", () => pickSituation(item));
      li.appendChild(button);
      el.situationChips.appendChild(li);
    }
    pickSituation(situations[0], { focus: false });
  }

  /* PICKING ONE REWRITES THE MESSAGE, and that is deliberate rather than convenient: a
   * message only means anything against the thing it is about, and carrying an edited one
   * across to another situation would put somebody's words under somebody else's heading. */
  /* THE SECOND ROW: which piece of news, for the situation picked above.
   *
   * Picking a situation redraws it and takes its first message, because a message only
   * means anything against the thing it is about - carrying one across would put this
   * business's "running late" under "missed call". The visitor's own "something else" has
   * no news to choose from, so the row goes rather than showing one empty chip. */
  function renderMessages(item, { focus }) {
    const messages = item.messages || [];
    el.messageChips.replaceChildren();
    el.messageChips.hidden = messages.length < 2;
    messages.forEach((message, i) => {
      const li = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "message-chip";
      button.textContent = message.label;
      button.dataset.message = String(i);
      button.setAttribute("aria-pressed", "false");
      button.addEventListener("click", () => pickMessage(i));
      li.appendChild(button);
      el.messageChips.appendChild(li);
    });
    if (messages.length) pickMessage(0);
    else el.cardMessage.textContent = "";
    if (item.custom && focus) el.cardContext.focus();
  }

  function pickMessage(index) {
    const messages = (situation && situation.messages) || [];
    const picked = messages[index];
    if (!picked) return;
    el.cardMessage.textContent = picked.text;
    for (const button of el.messageChips.querySelectorAll(".message-chip")) {
      button.setAttribute("aria-pressed", String(Number(button.dataset.message) === index));
    }
  }

  function pickSituation(item, { focus = true } = {}) {
    situation = item;
    el.cardContext.textContent = item.context;
    for (const button of el.situationChips.querySelectorAll(".situation-chip")) {
      button.setAttribute("aria-pressed", String(button.dataset.situation === item.id));
    }
    // THE CONTEXT IS ONLY EDITABLE ON "SOMETHING ELSE". On a drafted situation it is the
    // spec's wording, written on the review screen, and the server reads it off the spec
    // rather than off the request - so a box that invited typing would be a box whose
    // edits went nowhere.
    el.cardContext.contentEditable = item.custom ? "plaintext-only" : "false";
    el.cardContext.classList.toggle("is-editable", Boolean(item.custom));
    // An empty box with no hint is a box nobody knows what to put in. `.field:empty::before`
    // draws it; a drafted situation is never empty, so it needs none.
    el.cardContext.dataset.placeholder = item.custom
      ? "I rang yesterday and nobody picked up."
      : "";
    // The chip hands the boxes over, so it puts the cursor where they are about to type -
    // unless it arrived by being the first chip on a freshly drawn card.
    renderMessages(item, { focus });
  }

  function setMode(next) {
    if (mode === next) return;
    mode = next;
    rememberMode(next);
    el.modeIn.setAttribute("aria-pressed", String(next === "in"));
    el.modeOut.setAttribute("aria-pressed", String(next === "out"));
    el.outboundCard.hidden = next !== "out";
    el.openers.hidden = next === "out" || !(config.page && (config.page.openers || []).length);
    el.dialError.hidden = true;
    el.dialErrorOut.hidden = true;
    el.dialStatus.hidden = true;
    writeLede();
    if (closedMessage) return;
    show(idleScreen());
  }

  /** What the card says, as the dial route takes it.
   *
   * `context` is sent ONLY on "something else", and the route reads it only there: on a
   * drafted situation the line belongs to the spec, and sending a copy of it would be a
   * second source for the one thing the review screen owns. */
  function readBrief() {
    const brief = {
      situation_id: situation ? situation.id : "",
      contact_name: (el.cardName.textContent || "").trim(),
      message: (el.cardMessage.textContent || "").trim(),
    };
    if (situation && situation.custom) {
      brief.context = (el.cardContext.textContent || "").trim();
    }
    return brief;
  }

  /** Nothing on the card may be edited while a call is in flight or in progress. */
  function lockCard(locked) {
    el.dialButton.disabled = locked;
    el.cardName.contentEditable = locked ? "false" : "plaintext-only";
    el.cardMessage.contentEditable = locked ? "false" : "plaintext-only";
    // Only ever editable on "something else", so unlocking restores what the chip decided
    // rather than turning the spec's own line into a box.
    const customNow = Boolean(situation && situation.custom);
    el.cardContext.contentEditable = !locked && customNow ? "plaintext-only" : "false";
    for (const button of el.situationChips.querySelectorAll(".situation-chip")) {
      button.disabled = locked;
    }
    for (const button of el.messageChips.querySelectorAll(".message-chip")) {
      button.disabled = locked;
    }
    el.modeIn.disabled = locked;
    el.modeOut.disabled = locked;
  }

  /* ── screens ────────────────────────────────────────────────────── */

  function show(name) {
    for (const [key, node] of Object.entries(el.screens)) node.hidden = key !== name;
  }

  function fail(node, message) {
    node.textContent = message;
    node.hidden = false;
  }

  /* The demo itself has gone - expired, paused or deleted while this page sat open. Every
   * screen goes and the page-level line stays, which is the dead end `/call/<slug>` would
   * have served (functions/_closed.js) had the visitor arrived a minute later. Putting the
   * dial button back would only ring into the same 410. */
  let closedMessage = "";

  function closeThePage(message) {
    closedMessage = message;
    for (const node of Object.values(el.screens)) node.hidden = true;
    fail(el.pageError, message);
  }

  /* ── captions ───────────────────────────────────────────────────── */

  let callerLive = null;
  let agentLive = null;
  let agentBuffer = "";
  const caption = createCaptions(el.captions);

  /* ── live call state ────────────────────────────────────────────── */

  let client = null;
  let audioCtx = null;
  let remoteSource = null;
  let remoteAnalyser = null;
  let micSource = null;
  let micAnalyser = null;
  let timerTimer = null;
  let stopWave = null;
  let startedAt = 0;
  let ended = false;
  let isConnected = false;
  let returnTimer = null;
  let callT0 = 0;

  const ringer = createRinger();
  const waveSources = () => [remoteAnalyser, micAnalyser];

  /* ── audio ──────────────────────────────────────────────────────────
   * The bot's voice MUST be played through the <audio> element. That is the path the
   * browser's AEC uses as its reference signal. Route it through audioCtx.destination
   * instead and Chrome's AEC flatlines: the agent hears itself, transcribes itself, and
   * interrupts itself. `tap()` takes the analysers off both tracks and connects them to
   * nothing. */

  function attachBotAudio(track) {
    const stream = new MediaStream([track]);
    el.remoteAudio.srcObject = stream;
    el.remoteAudio.play().catch(() => {});
    const tapped = tap(audioCtx, track);
    if (tapped) ({ source: remoteSource, analyser: remoteAnalyser } = tapped);
  }

  function attachMicAnalyser(track) {
    if (micAnalyser) return;
    const tapped = tap(audioCtx, track);
    if (tapped) ({ source: micSource, analyser: micAnalyser } = tapped);
  }

  /* ── the call ───────────────────────────────────────────────────── */

  function setCallState(text) {
    el.callState.textContent = text;
  }

  // The cold-start script. One warm pod serves every demo (pcc-deploy.toml), so this is
  // rarely seen - but a ringing screen that says nothing for ten seconds reads as a hang.
  const RING_NOTES = [
    [3, "Waking the line — the first call takes about ten seconds."],
    [8, "Starting the agent…"],
    [13, "Loading its voice…"],
    [18, "Nearly there — when it picks up, just talk."],
    [28, "Taking longer than usual. Hang on a moment."],
    [45, "Still not through. Hang up and try again."],
  ];
  const ringNotes = createNotes(el.ringNote, RING_NOTES);

  function resetCallScreen() {
    el.phoneScreen.classList.remove("is-live");
    el.liveDisc.classList.remove("is-connected", "is-agent", "is-caller");
    el.liveDisc.classList.add("is-ringing");
    el.callState.hidden = false;
    setCallState("calling…");
    el.speakingLabel.textContent = "ringing";
    el.callTimer.hidden = true;
    el.callTimer.textContent = "00:00";
    el.islandTimer.textContent = "00:00";
    el.limitNote.hidden = true;
    ringNotes.clear();
    el.muteButton.setAttribute("aria-pressed", "false");
    el.muteLabel.textContent = "mute";
  }

  function returnToDialer() {
    clearTimeout(returnTimer);
    returnTimer = null;
    resetCallScreen();
    lockCard(false);
    el.dialStatus.hidden = true;
    if (closedMessage) return closeThePage(closedMessage);
    show(idleScreen());
  }

  /* `mic` IS THE WHOLE OF THE DIFFERENCE between the two directions' clients.
   *
   * Inbound the visitor pressed Call, so the microphone opens with the connection and
   * `onBotReady` means the call has started. Outbound the bot joins an empty room and
   * WAITS to be answered: the microphone stays shut until Answer is tapped, and bot-ready
   * means only that it is ready to ring - which is exactly when `dialOut` starts the
   * ringer, off `connect()` resolving rather than off this callback. */
  function buildClient({ mic } = { mic: true }) {
    return new PipecatClient({
      transport: new DailyTransport({
        // Deliberately NOT bufferLocalAudioUntilBotReady: the agent mutes the caller until
        // the greeting completes, so buffered audio is discarded server-side anyway.
        inputSettings: {
          audio: {
            settings: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          },
        },
      }),
      enableMic: mic,
      enableCam: false,
      callbacks: {
        // Outbound, the call has not started yet: somebody has to pick the phone up.
        onBotReady: () => { if (mode === "in") connected(); },

        onUserStartedSpeaking: () => {
          el.liveDisc.classList.add("is-caller");
          el.liveDisc.classList.remove("is-agent");
          el.speakingLabel.textContent = "Listening";
        },
        onUserStoppedSpeaking: () => {
          el.liveDisc.classList.remove("is-caller");
          el.speakingLabel.textContent = "On the call";
        },

        onBotStartedSpeaking: () => {
          connected();
          el.liveDisc.classList.add("is-agent");
          el.liveDisc.classList.remove("is-caller");
          el.speakingLabel.textContent = "Speaking";
          agentBuffer = "";
          agentLive = caption(business, "caption-agent");
        },
        onBotStoppedSpeaking: () => {
          el.liveDisc.classList.remove("is-agent");
          el.speakingLabel.textContent = "On the call";
          agentLive = null;
        },

        onBotTtsText: (data) => {
          if (agentLive && data && data.text) {
            agentBuffer += (agentBuffer ? " " : "") + data.text;
            agentLive.textContent = agentBuffer;
            el.captions.scrollTop = el.captions.scrollHeight;
          }
        },
        onBotOutput: (data) => {
          if (agentLive && !agentBuffer && data && data.text) {
            agentLive.textContent = data.text;
          }
        },

        onUserTranscript: (data) => {
          if (!data || !data.text) return;
          if (!callerLive) callerLive = caption("You", "caption-caller");
          callerLive.textContent = data.text;
          el.captions.scrollTop = el.captions.scrollHeight;
          if (data.final) callerLive = null;
        },

        /* No read-out on this page: the creator shows the lead card after the call. The
         * one message that does arrive here is the pod saying it is ready to ring
         * (`handset.agent.outbound.RINGING_MESSAGE`) - the page does not wait for it,
         * `connect()` resolving is the signal, so this is a cross-check and a log line. */
        onServerMessage: (data) => {
          if (data && data.type === "outbound-ringing") console.info("the assistant is ready");
        },

        onTrackStarted: (track, participant) => {
          if (track.kind !== "audio") return;
          if (participant && participant.local) attachMicAnalyser(track);
          else attachBotAudio(track);
        },

        onDeviceError: (error) => {
          console.warn("device error", error);
          hangUp("This needs your microphone. Allow access and try again.");
        },

        onBotDisconnected: () => hangUp(null),
        onDisconnected: () => hangUp(null),
        onError: (message) => console.warn("agent error", message),
        onMessageError: (message) => console.warn("agent message error", message),
      },
    });
  }

  /** What a mint that failed means, from wherever the page is standing. */
  function mintFailed({ status }) {
    if (status === 410) return closeThePage("This demo is no longer live.");
    if (status === 404) return closeThePage("There's no demo at this link.");
    if (status === 401 && config.passcodeRequired) {
      // The only thing /api/session refuses with a 401 is the passcode, and this one was
      // right when the visitor typed it. It has been rotated or switched off since.
      passcode = "";
      show("gate");
      fail(el.gateError, "That passcode is not right any more. Check the code you were sent.");
      el.passcode.value = "";
      el.passcode.focus();
      return;
    }
    // Unconfigured, unreachable, or a 500. Recoverable, and the button is right there:
    // pressing it again mints again, which is the whole of what this buys.
    lockCard(false);
    show(idleScreen());
    fail(mode === "out" ? el.dialErrorOut : el.dialError,
         "Could not reach the demo server. Try again.");
  }

  /** Stop what the call screen started WITHOUT the ended screen: the caller is about to put
   *  a different one up, and hangUp() would schedule a return to the dialer over the top. */
  function abandonCall() {
    ended = true;
    clearTimeout(returnTimer);
    returnTimer = null;
    teardown();
    resetCallScreen();
  }

  async function placeCall() {
    isConnected = false;
    el.dialError.hidden = true;
    ringNotes.clear();
    if (config.passcodeRequired && !passcode) {
      // Gated demo, and this visitor has not given the code yet. Ask before spending a
      // round trip on a mint that can only be refused.
      show("gate");
      el.passcode.focus();
      return;
    }

    // BEFORE THE FIRST AWAIT, while this tap still counts (rule ONE in call-kit.js). The
    // play() has no source yet; what it buys is the element's leave to play the bot's
    // track when that arrives, seconds from now. A context left open by a failed mint is
    // reused rather than leaked.
    if (!audioCtx) audioCtx = openAudioContext();
    el.remoteAudio.play().catch(() => {});

    // MINTED HERE, a moment before it is spent. A page that has been open since this
    // morning is the normal case for a demo link, not the exception.
    const minted = await sessionToken.get();
    if (!minted.ok) return mintFailed(minted);

    ended = false;
    clearTimeout(returnTimer);
    returnTimer = null;
    show("call");
    resetCallScreen();
    callT0 = performance.now();
    ringNotes.schedule(callT0);
    el.captions.replaceChildren();
    startedAt = 0;

    let session;
    try {
      let res = await postJson("/api/connect", { slug, token: minted.token, visit });
      if (res.status === 401) {
        // The edge refused a token this page thought was live: a browser clock further
        // behind than the margin, or a secret rotated under us. One re-mint and one retry,
        // because nobody should pay a reload for either. A second 401 is a real refusal.
        sessionToken.clear();
        const again = await sessionToken.get();
        if (!again.ok) {
          abandonCall();
          return mintFailed(again);
        }
        res = await postJson("/api/connect", { slug, token: again.token, visit });
      }
      if (res.status === 503) return hangUp("The demo line is busy. Try again shortly.");
      if (res.status === 401) return hangUp("Couldn't start the call. Try again.");
      if (res.status === 410 || res.status === 404) {
        closedMessage = res.status === 410
          ? "This demo is no longer live."
          : "There's no demo at this link.";
        return hangUp(closedMessage);
      }
      if (!res.ok) return hangUp(`Couldn't get through to ${business}.`);
      session = await res.json();
    } catch (err) {
      console.error(err);
      return hangUp(`Couldn't get through to ${business}.`);
    }

    ringer.start(audioCtx, "ringback");
    setCallState("ringing…");

    client = buildClient();
    try {
      await client.connect(session);
      reportAudioConstraints(client);
    } catch (err) {
      console.error(err);
      hangUp(`Couldn't get through to ${business}.`);
    }
  }

  /* ── the other direction ──────────────────────────────────────────
   * The handset rings at you. The order below is the whole of the design and every line
   * of it was earned on the autofix page (`client/src/outbound.js`):
   *
   *   mint -> lock the card -> POST /api/dial -> connect() -> ONLY THEN ring.
   *
   * `connect()` RESOLVES ON BOT-READY, which is why the ring waits for it: the pipeline
   * is up and the agent can speak the instant it is answered. A phone that rings while
   * the container is still coming up is a phone you answer into ten seconds of silence.
   * The loader line covers that wait instead, where it can say what is happening. */

  // The cold-start script for the wait BEFORE the ring. `min_agents` stays 0 on this
  // deployment, so the first call of the day pays for a container coming up.
  const DIAL_NOTES = [
    [3, "Waking the line — the first call takes about ten seconds."],
    [8, "Starting the assistant…"],
    [13, "Loading its voice…"],
    [18, "Nearly there — your phone will ring in a moment."],
    [28, "Taking longer than usual. Hang on."],
    [45, "Still not through. Try again."],
  ];
  const dialNotes = createNotes(el.dialStatus, DIAL_NOTES);
  let answerTimer = null;
  let dialling = false;

  function dialFailed(message) {
    dialling = false;
    dialNotes.clear();
    el.dialStatus.hidden = true;
    lockCard(false);
    fail(el.dialErrorOut, message);
    teardown();
    show("idle");
  }

  async function dialOut() {
    if (dialling) return;
    const brief = readBrief();
    el.dialErrorOut.hidden = true;
    if (!brief.situation_id) {
      return fail(el.dialErrorOut, "Pick a reason for the call first.");
    }
    if (!brief.contact_name) {
      return fail(el.dialErrorOut,
                  "Put your name on the card - the assistant asks for it when you answer.");
    }
    if (situation && situation.custom && !brief.context) {
      el.cardContext.focus();
      return fail(el.dialErrorOut, "Say what the call is about.");
    }
    if (!brief.message) {
      return fail(el.dialErrorOut, "There's nothing for the assistant to say.");
    }
    if (config.passcodeRequired && !passcode) {
      show("gate");
      el.passcode.focus();
      return;
    }

    // BEFORE THE FIRST AWAIT, while this tap still counts - the same rule placeCall()
    // follows, and for the same reason (rule ONE in call-kit.js).
    if (!audioCtx) audioCtx = openAudioContext();
    el.remoteAudio.play().catch(() => {});

    // MINTED HERE, a moment before it is spent - the same rule placeCall() follows.
    const minted = await sessionToken.get();
    if (!minted.ok) return mintFailed(minted);

    dialling = true;
    ended = false;
    isConnected = false;
    lockCard(true);
    clearTimeout(returnTimer);
    returnTimer = null;
    resetCallScreen();
    show("idle");
    el.captions.replaceChildren();
    startedAt = 0;
    // The sweep is back on: this is a new wait, not the finished one the last call left.
    el.dialStatus.classList.remove("is-done");
    dialNotes.schedule(performance.now());

    let session;
    try {
      let res = await postJson("/api/dial", { slug, token: minted.token, visit, ...brief });
      if (res.status === 401) {
        // The edge refused a token this page thought was live: a browser clock further
        // behind than the margin, or a secret rotated under us. One re-mint and one retry.
        sessionToken.clear();
        const again = await sessionToken.get();
        if (!again.ok) {
          dialling = false;
          lockCard(false);
          dialNotes.clear();
          el.dialStatus.hidden = true;
          return mintFailed(again);
        }
        res = await postJson("/api/dial", { slug, token: again.token, visit, ...brief });
      }
      if (res.status === 503) return dialFailed("The demo line is busy. Try again shortly.");
      if (res.status === 401) return dialFailed("Couldn't start the call. Try again.");
      if (res.status === 422) {
        const body = await res.json().catch(() => ({}));
        return dialFailed(body.detail || "The card is missing something.");
      }
      if (res.status === 410 || res.status === 404) {
        closedMessage = res.status === 410
          ? "This demo is no longer live."
          : "There's no demo at this link.";
        return dialFailed(closedMessage);
      }
      if (!res.ok) return dialFailed(`Couldn't get ${business} to ring you.`);
      session = await res.json();
    } catch (err) {
      console.error(err);
      return dialFailed(`Couldn't get ${business} to ring you.`);
    }

    client = buildClient({ mic: false });
    try {
      await client.connect(session);
    } catch (err) {
      console.error(err);
      return dialFailed(`Couldn't get ${business} to ring you.`);
    }
    startRinging(brief.contact_name);
  }

  function startRinging(name) {
    dialling = false;
    dialNotes.clear();
    el.dialStatus.hidden = false;
    el.dialStatus.textContent = `Ringing ${name}…`;
    el.dialStatus.classList.add("is-done");
    el.ringingName.textContent = business;

    show("ringing");
    ringer.start(audioCtx, "incoming");

    // Answering during the first ring cycle would be answering a phone that has not
    // finished making its first sound. The buttons are unavailable for exactly that long,
    // which also gives the visitor a beat to notice the phone is ringing at all.
    el.answerButton.disabled = true;
    el.declineButton.disabled = true;
    answerTimer = setTimeout(() => {
      el.answerButton.disabled = false;
      el.declineButton.disabled = false;
    }, 2200);
  }

  function answer() {
    if (!client) return;
    clearTimeout(answerTimer);
    ringer.stop();
    el.dialStatus.textContent = "On the call.";
    show("call");

    // Answer is a tap of its own, the last one before the agent speaks. Unlocking again
    // here covers a browser that let the dial tap's leave lapse during the ring.
    if (audioCtx) audioCtx.resume().catch(() => {});
    el.remoteAudio.play().catch(() => {});

    // IN THIS ORDER, and the order matters: the microphone has to be live before the
    // agent starts talking, or the first thing said back to it is lost.
    client.enableMic(true);
    reportAudioConstraints(client);
    // Guarded because it throws on a transport that is not ready, and a throw here would
    // leave the page showing a live call with nothing on the other end. The handset only
    // rings once `connect()` has resolved, so this is the case where the connection
    // dropped between the ring and the tap.
    try {
      client.sendClientMessage("answer");
    } catch (err) {
      console.warn(err);
      return hangUp(`Couldn't get through to ${business}.`);
    }
    connected();
  }

  function decline() {
    if (!client) return hangUp("Declined.");
    clearTimeout(answerTimer);
    ringer.stop();
    // The bot ends the session itself, so the record says "declined" rather than
    // "disconnected" - the difference between a call that was refused and one that fell
    // over. If the transport is already gone, hanging up is the whole of what is left.
    try {
      client.sendClientMessage("decline");
    } catch (err) {
      console.warn(err);
    }
    hangUp("Declined.");
  }

  function connected() {
    if (isConnected) return;
    isConnected = true;
    ringer.stop();
    ringNotes.clear();
    el.liveDisc.classList.remove("is-ringing");
    el.liveDisc.classList.add("is-connected");
    el.callState.hidden = true;
    el.callTimer.hidden = false;
    el.phoneScreen.classList.add("is-live");
    el.speakingLabel.textContent = "on the call";
    startedAt = Date.now();
    stopWave = startWave(el.wave, accentColour("#4fb0ff"), waveSources);

    timerTimer = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAt) / 1000);
      const reading = clock(secs);
      el.callTimer.textContent = reading;
      el.islandTimer.textContent = reading;
      if (config.maxCallSeconds > 0) {
        const left = config.maxCallSeconds - secs;
        if (left <= 60 && left > 0) {
          el.limitNote.hidden = false;
          el.limitNote.textContent = `This demo call ends in ${left}s.`;
        }
      }
    }, 500);
  }

  function teardown() {
    clearInterval(timerTimer);
    timerTimer = null;
    clearTimeout(answerTimer);
    answerTimer = null;
    dialling = false;
    ringer.stop();
    ringNotes.clear();
    if (stopWave) stopWave();
    stopWave = null;
    if (client) {
      client.disconnect().catch(() => {});
      client = null;
    }
    if (audioCtx) audioCtx.close().catch(() => {});
    el.remoteAudio.srcObject = null;
    audioCtx = remoteSource = remoteAnalyser = micSource = micAnalyser = null;
  }

  function hangUp(reason) {
    if (ended) return;
    ended = true;
    const secs = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0;
    teardown();

    el.phoneScreen.classList.remove("is-live");
    el.endedEyebrow.textContent = secs ? `Call Ended · ${clock(secs)}` : "Call Ended";
    el.endedTitle.textContent = business;
    el.endedSub.textContent = reason || "";
    show("ended");
    returnTimer = setTimeout(returnToDialer, reason ? 4000 : 2400);
  }

  /* ── wiring ─────────────────────────────────────────────────────── */

  el.callButton.addEventListener("click", placeCall);
  el.endButton.addEventListener("click", () => hangUp(null));
  el.modeIn.addEventListener("click", () => setMode("in"));
  el.modeOut.addEventListener("click", () => setMode("out"));
  el.dialButton.addEventListener("click", dialOut);
  el.answerButton.addEventListener("click", answer);
  el.declineButton.addEventListener("click", decline);

  el.muteButton.addEventListener("click", () => {
    if (!client) return;
    const wasEnabled = client.isMicEnabled;
    client.enableMic(!wasEnabled);
    el.muteButton.setAttribute("aria-pressed", String(wasEnabled));
    el.muteLabel.textContent = wasEnabled ? "unmute" : "mute";
  });

  el.gateForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    el.gateError.hidden = true;
    // The gate IS a mint: /api/session checks sha256(passcode + demo id) against the row and
    // answers 401 when it does not match. What is kept afterwards is the code as well as the
    // token, so a call placed three hours from now can mint again without asking the visitor
    // for a number they no longer have in front of them.
    passcode = el.passcode.value;
    const minted = await sessionToken.get();
    if (minted.ok) return show(idleScreen());

    passcode = "";
    if (minted.status === 410) return closeThePage("This demo is no longer live.");
    if (minted.status === 404) return closeThePage("There's no demo at this link.");
    fail(el.gateError, minted.status === 401
      ? "That passcode is not right."
      : "Could not reach the demo server. Try again.");
    el.passcode.select();
  });

  window.addEventListener("beforeunload", teardown);

  /* ── start ──────────────────────────────────────────────────────── */

  (async () => {
    dress({ name: boot.business && boot.business.name, page: boot.brand ? { brand: boot.brand } : null });
    try {
      const res = await fetch(`/api/config${slug ? `?slug=${encodeURIComponent(slug)}` : ""}`);
      if (res.status === 404) {
        fail(el.pageError, "There's no demo at this link.");
        return;
      }
      config = await res.json();
    } catch (_) {
      fail(el.dialError, "Could not reach the demo server.");
    }
    if (config.business) {
      dress({ name: config.business.name, kind: config.business.kind, page: config.page });
    }
    // Normally unreachable: `functions/call/[slug].js` answers a non-live slug with the
    // closed page and this bundle is never served. This catches the demo that lapses in
    // the seconds between the page loading and /api/config answering.
    if (config.expired || config.paused) {
      fail(el.pageError, config.expired ? "This demo has closed." : "This demo is paused for now.");
      return;
    }
    // THE SWITCH ONLY EXISTS WHERE THE OTHER SIDE WORKS. `outbound.web` is the published
    // spec having situations, read per request - see functions/api/config.js.
    if (config.outbound && config.outbound.web && (config.situations || []).length) {
      el.modeSwitch.hidden = false;
      el.cardBusiness.textContent = business;
      // FILLED IN, not a placeholder. The card has to be ringable on sight - a visitor who
      // taps it as it stands should get a call, not a validation error - and the name is
      // the one field where an empty box reads as "optional". They change it if they want
      // to hear their own name; the assistant greets whoever is written here.
      el.cardName.textContent = DEFAULT_NAME;
      renderSituations();
      if (wantsOutbound()) setMode("out");
    }
    if (config.passcodeRequired) {
      show("gate");
      el.passcode.focus();
    } else {
      // The usual path: no passcode on this demo, so there is nothing to ask for and
      // nothing to mint yet - the token is minted when the button is pressed. (Same route
      // on a laptop, where server.py runs with no DEMO_PASSCODE and the SDK's demo server
      // mints an unsigned token, and answers the same {token, expiresIn}.)
      // `idleScreen()`, not "dialer": on a link opened with `?mode=outbound` the phone is
      // waiting to be rung, and the dialer's green button would be a second way to start
      // a call the visitor did not ask for.
      show(idleScreen());
    }
  })();
})();
